import React, { memo, useEffect } from "react";

const TAMARA_SCRIPT_SRC =
  process.env.REACT_APP_TAMARA_WIDGET_URL ||
  "https://cdn.tamara.co/widget-v2/tamara-widget.js";

const PUBLIC_KEY = process.env.REACT_APP_TAMARA_PUBLIC_KEY;

// Tamara v2 reads window.tamaraWidgetConfig at script-init time. Set it
// once, BEFORE the <script> tag is appended, so the SDK can pick it up.
const setTamaraConfig = (lang) => {
  if (typeof window === "undefined") return;
  window.tamaraWidgetConfig = {
    lang: lang === "ar" ? "ar" : "en",
    country: "SA",
    publicKey: PUBLIC_KEY,
  };
};

let tamaraScriptPromise = null;

const loadTamaraScript = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (tamaraScriptPromise) return tamaraScriptPromise;

  tamaraScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TAMARA_SCRIPT_SRC}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = TAMARA_SCRIPT_SRC;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return tamaraScriptPromise;
};

// Per-element styling options passed as a JSON string in the `config`
// attribute (per Tamara v2 spec).
//
// DEFAULT — the original full summary used on the bundle detail page and the
// cart: the v2 bundle widget ("Pay X SAR/mo or in 4 payments. Sharia-compliant.
// More options" + brandmark).
const DEFAULT_CONFIG = JSON.stringify({
  theme: "light",
  badgePosition: "",
  showExtraContent: "",
  hidePayInX: false,
});

// COMPACT — the one-line text variant used on the Home bundle cards
// (inline-type="0" + inline-variant="text" + the text child below). Badge sits
// on the right; hidePayInX drops the "pay in X" sentence so only our short line
// shows.
const COMPACT_CONFIG = JSON.stringify({
  theme: "light",
  badgePosition: "right",
  showExtraContent: "",
  hidePayInX: true,
});

// Text shown in the compact one-liner. {{badgeTamara}} is replaced by the SDK
// with the Tamara brandmark. NOTE (please verify): Arabic copy is a translation
// of the English line — confirm wording with the client if needed.
const COMPACT_TEXT = {
  // en: "No late fees, Sharia compliant {{badgeTamara}}",
  // ar: "بدون رسوم تأخير، متوافق مع الشريعة {{badgeTamara}}",
  en: "Split Payment {{badgeTamara}}",
  ar: "تقسيط الدفع {{badgeTamara}}"
};

const TamaraWidget = ({ amount, lang = "en", className = "", compact = false, onlyLearnMore = false }) => {
  const numericAmount = Number(amount);
  const hasValidAmount = Number.isFinite(numericAmount) && numericAmount > 0;
  const canRender = hasValidAmount && Boolean(PUBLIC_KEY);

  useEffect(() => {
    if (!canRender) return;
    // Must run before loadTamaraScript so the SDK sees the config on init.
    setTamaraConfig(lang);

    let cancelled = false;
    loadTamaraScript()
      .then(() => {
        if (cancelled) return;
        if (window.TamaraWidgetV2 && typeof window.TamaraWidgetV2.refresh === "function") {
          window.TamaraWidgetV2.refresh();
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canRender, numericAmount, lang]);

  if (!canRender) return null;

  // key={numericAmount} forces a remount when the amount changes — Tamara's
  // <tamara-widget> only observes ['uuid'], so attribute updates after first
  // paint are otherwise ignored.
  return (
    <div className={className}>
      {compact ? (
        // Home bundle cards: one-line text variant.
        <tamara-widget
          key={numericAmount}
          type="tamara-summary"
          amount={String(numericAmount)}
          inline-type="0"
          inline-variant="outlined"
          config={COMPACT_CONFIG}
        >
          <>{lang === "ar" ? COMPACT_TEXT.ar : COMPACT_TEXT.en}</>
          
        </tamara-widget>
      ) : (
        // Detail page + cart: original full v2 bundle summary.
        <tamara-widget
          key={numericAmount}
          type="tamara-summary"
          amount={String(numericAmount)}
          inline-type="2"
          inline-variant="outlined"
          config={DEFAULT_CONFIG}
        />
      )}
    </div>
  );
};

// Memoised so frequent parent re-renders (Home.js, MyCart, BundlDetail)
// don't re-render the widget when amount/lang/className haven't changed —
// every render otherwise triggers Tamara's attributeChangedCallback and
// re-validates the key.
export default memo(TamaraWidget);
